import { Injectable } from "@angular/core";
import gql from "graphql-tag";
import { Apollo } from "apollo-angular";
import { Guide } from "../models/guide";

@Injectable()
export class GuidesService {
  constructor(private apollo: Apollo) {}

  getAllGuides(obj?) {
    let variables = {};
    console.log("obj", obj);

    if (obj) {
      variables = {
        first: obj.paginationData.pageSize || 10, // Default to 10 if pageSize is not provided
        after: obj.paginationData.after
          ? obj.paginationData.after.endCursor
          : null, // Use null if no cursor is provided
        searchParam: obj.searchParam || null, // Use null if no searchParam is provided
      };
    }

    return this.apollo.watchQuery({
      query: gql`
        query GetGuides($first: Int, $after: String, $searchParam: String) {
          guides(first: $first, after: $after, searchParam: $searchParam) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                name
                slug
                type
                licenseNumber
                headshot {
                  id
                  url
                  path
                  filename
                  mimetype
                }
                languages {
                  id
                }
                photos {
                  id
                  url
                  originalUrl
                  path
                  filename
                  mimetype
                }
                video {
                  id
                  url
                  path
                  filename
                  mimetype
                }
              }
              cursor
            }
            totalCount
          }
        }
      `,
      variables,
      errorPolicy: "all",
    }).valueChanges;
  }

  createGuide(guide: Guide) {
    guide.slug = " ";
    return this.apollo.mutate({
      mutation: gql`
        mutation createGuide($name: String!, $slug: String!, $cid: String) {
          createGuide(guide: { name: $name, slug: $slug, cid: $cid }) {
            id
            name
            slug
          }
        }
      `,
      variables: {
        name: guide.name,
        slug: guide.slug,
        cid: guide.cid,
      },
    });
  }

  getGuide(id: string) {
    return this.apollo.watchQuery({
      query: gql`
          {
            guide(id: "${id}") {
              id
              name
              slug
              published
              cid
              licenseNumber
              dob
              originallyFrom {
                id
              }
              languages {
                id
                name
              }
              tags {
                id
                name
              }
              organizations {
                id
                name
              }
              districts {
                id
                name
              }
              mailingAddress
              phoneNumber
              emailAddress
              type
              tcbCertified
              gabCertified
              gender
              modelRelease
              additionalFee
              education
              yearOfGraduation
              university
              major
              driversLicense
              strengths
              hobbiesString
              favoriteSites
              why
              uniqueness
              favoriteThing
              bestExperience
              touristDescription
              specialAboutBhutan
              whyVisitBhutan
              challenges
              whyIGuide
              specializations
              trekking
              dzongkhaName
              hobbies
              awards
              certifications
              publications {
                title
                url
              }
              featured_in {
                title
                url
              }
              overallRating
              savesCount
              interestsCount
              reviews {
                id
                clientName
                clientLocation
                status
              }
              photos {
                id
                url
                path
                filename
                mimetype
                credit
                caption
                editor
              }
              video {
                id
                url
                path
                filename
                mimetype
                credit
                caption
                editor
              }
              legalDocs{
                id
                path
                url
                filename
                mimetype
                legal

              }
            }
          }
        `,
    }).valueChanges;
  }

  getGuideMetaData() {
    return this.apollo.watchQuery({
      query: gql`
        {
          availableSpecializations {
            key
            name
          }
          availableTreks {
            key
            name
          }
          availableGenders {
            key
            name
          }
          availableTypes {
            key
            name
          }
          availableEducationType {
            key
            name
          }
        }
      `,
    }).valueChanges;
  }

  updateGuide(guide) {
    const languages = [],
      tags = [],
      organizations = [],
      districts = [];
    if (guide.languages) {
      guide.languages.forEach((lang) => languages.push({ id: lang.id }));
    }
    if (guide.tags) {
      guide.tags.forEach((tag) => tags.push({ id: tag.id }));
    }
    if (guide.organizations) {
      guide.organizations.forEach((organization) =>
        organizations.push({ id: organization.id }),
      );
    }
    if (guide.districts) {
      guide.districts.forEach((district) =>
        districts.push({ id: district.id }),
      );
    }
    return this.apollo.mutate({
      mutation: gql`
        mutation updateGuide(
          $id: ID!
          $slug: String!
          $name: String
          $cid: String
          $published: Boolean
          $licenseNumber: String
          $dob: String
          $mailingAddress: String
          $phoneNumber: String
          $emailAddress: String
          $type: String
          $tcbCertified: Boolean
          $gabCertified: Boolean
          $modelRelease: Boolean
          $additionalFee: Int
          $gender: String
          $education: String
          $university: String
          $yearOfGraduation: Int
          $training: String
          $major: String
          $driversLicense: Boolean
          $strengths: String
          $hobbiesString: String
          $uniqueness: String
          $favoriteThing: String
          $favoriteSites: String
          $bestExperience: String
          $touristDescription: String
          $specialAboutBhutan: String
          $whyVisitBhutan: String
          $challenges: String
          $whyIGuide: String
          $why: String
          $supportiveOrganizations: String
          $cooking: String
          $groupsPerYear: Int
          $languages: [GuideLanguageInput]
          $specializations: [String]
          $trekking: [String]
          $tags: [GuideTagInput]
          $organizations: [GuideOrganizationInput]
          $districts: [GuideDistrictInput]
          $dzongkhaName: String
          $hobbies: [String]
          $awards: [String]
          $certifications: [String]
          $publications: [PublicationLinkInput]
          $featured_in: [PublicationLinkInput]
        ) {
          updateGuide(
            guide: {
              id: $id
              slug: $slug
              name: $name
              cid: $cid
              published: $published
              licenseNumber: $licenseNumber
              dob: $dob
              mailingAddress: $mailingAddress
              phoneNumber: $phoneNumber
              emailAddress: $emailAddress
              type: $type
              tcbCertified: $tcbCertified
              gabCertified: $gabCertified
              modelRelease: $modelRelease
              additionalFee: $additionalFee
              gender: $gender
              education: $education
              university: $university
              yearOfGraduation: $yearOfGraduation
              training: $training
              major: $major
              driversLicense: $driversLicense
              strengths: $strengths
              hobbiesString: $hobbiesString
              uniqueness: $uniqueness
              favoriteThing: $favoriteThing
              favoriteSites: $favoriteSites
              bestExperience: $bestExperience
              touristDescription: $touristDescription
              specialAboutBhutan: $specialAboutBhutan
              whyVisitBhutan: $whyVisitBhutan
              challenges: $challenges
              whyIGuide: $whyIGuide
              why: $why
              supportiveOrganizations: $supportiveOrganizations
              cooking: $cooking
              groupsPerYear: $groupsPerYear
              languages: $languages
              specializations: $specializations
              trekking: $trekking
              tags: $tags
              organizations: $organizations
              districts: $districts
              dzongkhaName: $dzongkhaName
              hobbies: $hobbies
              awards: $awards
              certifications: $certifications
              publications: $publications
              featured_in: $featured_in
            }
          ) {
            success
          }
        }
      `,
      variables: {
        id: guide.id,
        slug: guide.type,
        name: guide.name,
        cid: guide.cid,
        published: guide.published,
        licenseNumber: guide.licenseNumber,
        dob: guide.dob,
        mailingAddress: guide.mailingAddress,
        phoneNumber: guide.phoneNumber,
        emailAddress: guide.emailAddress,
        type: guide.type,
        tcbCertified: guide.tcbCertified,
        gabCertified: guide.gabCertified,
        modelRelease: guide.modelRelease,
        additionalFee: guide.additionalFee,
        gender: guide.gender,
        education: guide.education,
        university: guide.university,
        yearOfGraduation: guide.yearOfGraduation,
        training: guide.training,
        major: guide.major,
        driversLicense: guide.driversLicense,
        strengths: guide.strengths,
        hobbiesString: guide.hobbiesString,
        uniqueness: guide.uniqueness,
        favoriteThing: guide.favoriteThing,
        favoriteSites: guide.favoriteSites,
        bestExperience: guide.bestExperience,
        touristDescription: guide.touristDescription,
        specialAboutBhutan: guide.specialAboutBhutan,
        whyVisitBhutan: guide.whyVisitBhutan,
        challenges: guide.challenges,
        whyIGuide: guide.whyIGuide,
        supportiveOrganizations: guide.supportiveOrganizations,
        cooking: guide.cooking,
        groupsPerYear: guide.groupsPerYear,
        specializations: guide.specializations,
        trekking: guide.trekking,
        languages: languages,
        tags: tags,
        organizations: organizations,
        districts: districts,
        dzongkhaName: guide.dzongkhaName,
        hobbies: guide.hobbies,
        awards: guide.awards,
        certifications: guide.certifications,
        publications: guide.publications,
        featured_in: guide.featured_in,
      },
    });
  }

  updateHeadshot(guideId, imageId) {
    return this.apollo.mutate({
      mutation: gql`
        mutation updateHeadshot($guideId: ID!, $imageId: ID) {
          updateHeadshot(guideId: $guideId, imageId: $imageId) {
            success
          }
        }
      `,
      variables: {
        imageId: imageId,
        guideId: guideId,
      },
    });
  }
}



// WEBPACK FOOTER //
// /Users/ugyen/Documents/Bhutan-office/my_bhutan/mybhutan-travel/src-admin/app/services/guides.service.ts